import { useState } from "react";
import { motion } from "framer-motion";
import DotGrid from "./ui/dotGrid";
import Reveal from "./ui/reveal";

type Billing = "mensual" | "anual";

const plans = [
  {
    name: "Empresa",
    monthly: 90,
    flows: "1,5K",
    description:
      "Diseñado para organizaciones que requieren soberanía total. Crea automatizaciones propietarias para optimizar departamentos internos y proteger la integridad de tus datos críticos.",
    features: [
      "5 proyectos simultáneos",
      "5 usuarios",
      "Software Online 365 días",
      "30 espacios de trabajo con IA",
      "1 Asesoría de uso",
    ],
  },
  {
    name: "Agencia",
    monthly: 190,
    flows: "3,5K",
    description:
      "El motor de crecimiento para consultores y agencias. Implementa flujos de trabajo de alto valor para tus clientes bajo una infraestructura robusta que garantiza resultados y recurrencia.",
    features: [
      "12 proyectos simultáneos",
      "15 usuarios ilimitados",
      "Software Online 365 días",
      "60 espacios de trabajo con IA",
      "5 Asesorías de uso",
      "Plataforma de agencia",
    ],
  },
  {
    name: "Franquicia",
    monthly: 400,
    flows: "10K",
    description:
      'Modelo de negocio "llave en mano". Adquiere derechos de distribución, personaliza el software y comercializa tu propia red de automatizaciones. El poder de nuestra tecnología bajo tu propia visión comercial.',
    features: [
      "25 proyectos simultáneos",
      "Usuarios ilimitados",
      "Software Online 365 días",
      "Ilimitados espacios de trabajo con IA",
      "Ilimitada asesoría de uso",
      "Venta de licencia NODRIC a externos",
      "Plataforma de referidos Nodric",
    ],
  },
];

/** 25 -> "$25,00" (the mockup uses the comma as decimal separator). */
const formatPrice = (value: number) =>
  `$${value.toFixed(2).replace(".", ",")}`;

export default function Pricing() {
  const [billing, setBilling] = useState<Billing>("mensual");

  return (
    <section
      id="planes"
      className="relative overflow-hidden bg-gradient-to-b from-white to-[#eef4ff] py-20 md:py-28"
    >
      <DotGrid color="rgba(12,123,254,0.12)" size={30} />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <h2 className="font-display mx-auto max-w-[24ch] text-center text-2xl leading-snug font-medium tracking-[0.06em] text-nodric-ink md:text-4xl">
            Potencia interna para empresas que buscan el control absoluto de sus
            procesos.
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 flex justify-center">
          <div className="font-display flex items-center gap-3 rounded-full bg-nodric-ink p-1.5 pl-5">
            <span className="text-[10px] tracking-[0.2em] text-white/70 uppercase">
              Costos
            </span>
            <div className="relative flex rounded-full">
              {(["mensual", "anual"] as Billing[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setBilling(option)}
                  className="relative cursor-pointer rounded-full px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors"
                >
                  {billing === option && (
                    <motion.span
                      layoutId="billing-pill"
                      className="absolute inset-0 rounded-full bg-nodric-blue"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <span
                    className={`relative z-10 ${billing === option ? "text-white" : "text-white/60"}`}
                  >
                    {option}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {plans.map((plan, index) => {
            // Annual billing keeps two months free.
            const price =
              billing === "mensual" ? plan.monthly : plan.monthly * 10;

            return (
              <motion.article
                key={plan.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="flex flex-col rounded-xl border border-nodric-blue/25 bg-white/70 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-nodric-blue hover:shadow-[0_24px_60px_-24px_rgba(12,123,254,0.55)]"
              >
                <h3 className="font-display text-xl tracking-[0.14em] text-nodric-ink uppercase">
                  {plan.name}
                </h3>

                <div className="mt-5 flex items-baseline gap-2">
                  <motion.span
                    key={`${plan.name}-${billing}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="font-display text-3xl font-bold tracking-[0.02em] text-nodric-blue md:text-4xl"
                  >
                    {formatPrice(price)}
                  </motion.span>
                  <span className="font-display text-xs tracking-[0.1em] text-nodric-ink/60">
                    {billing}
                  </span>
                </div>

                <div className="mt-6 flex items-center gap-4 rounded-lg bg-nodric-blue px-5 py-4 text-white">
                  <span className="font-display text-2xl font-bold md:text-3xl">
                    {plan.flows}
                  </span>
                  <span className="font-display text-sm leading-tight tracking-[0.06em]">
                    Flujos de
                    <br />
                    trabajo
                  </span>
                </div>

                <p className="font-body mt-5 text-[13px] leading-relaxed text-nodric-ink/75">
                  {plan.description}
                </p>

                <ul className="font-body mt-5 flex-1 space-y-1.5 text-[13px] text-nodric-ink/80">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <span className="text-nodric-blue">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="font-display mt-8 cursor-pointer rounded-full bg-nodric-ink py-3 text-[11px] tracking-[0.2em] text-white uppercase transition-all duration-300 hover:bg-nodric-blue active:scale-[0.98]"
                >
                  Contratar
                </button>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
